import { useEffect, useRef, useState } from "react";
import { formatNumberToLocale } from "@utils/formatNumber";

import { buildCoverageViewModel } from "./disbursementCoverage";
import DisbursementCoverageOffcanvas from "./DisbursementCoverageOffcanvas";

const STATUS_TRIGGER_LABEL = Object.freeze({
  complete: "Coberto",
  partial: "Em ajuste",
  unavailable: "Indisponível",
  inconsistent: "Inconsistente",
});

const NEEDS_ATTENTION = new Set(["partial", "unavailable", "inconsistent"]);

const pct = (value, total) =>
  total > 0
    ? `${((value / total) * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`
    : "0,0%";

const money = (value) =>
  value == null ? "—" : `R$ ${formatNumberToLocale(value)}`;

const DisbursementCoverageCard = ({ coverage, projectId }) => {
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);
  const view = buildCoverageViewModel(coverage);
  if (!view.visible) return null;
  const needsAttention = NEEDS_ATTENTION.has(view.status);

  const railLabel =
    view.segments.length > 0
      ? view.segments
          .map(
            (segment) =>
              `${pct(
                segment.value,
                view.scheduledValue,
              )} ${segment.label.toLocaleLowerCase("pt-BR")}`,
          )
          .join("; ")
      : view.title;

  return (
    <>
      <div className="disbursement-coverage-chip-wrapper" ref={containerRef}>
        <button
          type="button"
          className={`disbursement-coverage-chip disbursement-coverage-chip--${view.status}${needsAttention ? " disbursement-coverage-chip--attention" : ""}`}
          aria-label="Cobertura do cronograma de desembolso"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="disbursement-coverage-chip__label">Cobertura</span>
          <strong>{STATUS_TRIGGER_LABEL[view.status] ?? "Não avaliada"}</strong>
          <span aria-hidden="true" className="disbursement-coverage-chip__caret">
            {open ? "▲" : "▼"}
          </span>
        </button>
        {open ? (
          <section className={`disbursement-coverage-card ${view.status}`}>
            <div className="disbursement-coverage-copy">
              <span className="coverage-eyebrow">
                Fluxo de caixa · cobertura do planejamento
              </span>
              <h3>{view.title}</h3>
              <p>{view.description}</p>
            </div>

            {view.segments.length > 0 && (
              <>
                <div className="disbursement-coverage-headline">
                  <div>
                    <span>Cobertura</span>
                    <strong>{view.percentageLabel}</strong>
                  </div>
                  <div>
                    <span>Fora do cronograma</span>
                    <strong>{money(view.gapValue)}</strong>
                  </div>
                </div>
                <div
                  className="disbursement-coverage-rail"
                  role="img"
                  aria-label={railLabel}
                >
                  {view.segments.map((segment) => (
                    <span
                      key={segment.key}
                      className={`coverage-segment ${segment.key}`}
                      style={{ width: `${segment.width}%` }}
                    />
                  ))}
                </div>
                <div className="disbursement-coverage-legend">
                  {view.segments.map((segment) => (
                    <div
                      key={segment.key}
                      className={segment.value > 0 ? undefined : "muted"}
                    >
                      <span className={`coverage-dot ${segment.key}`} />
                      <span>{segment.label}</span>
                      <strong>{money(segment.value)}</strong>
                    </div>
                  ))}
                </div>
              </>
            )}

            {view.canOpen && (
              <button
                className="button button-accent small"
                onClick={() => setOffcanvasOpen(true)}
              >
                Ver itens a ajustar
              </button>
            )}
          </section>
        ) : null}
      </div>

      <DisbursementCoverageOffcanvas
        open={offcanvasOpen}
        onClose={() => setOffcanvasOpen(false)}
        projectId={projectId}
      />
    </>
  );
};

export default DisbursementCoverageCard;
